import React, { useState } from 'react';
import axios from 'axios';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, Phone, MapPin, MessageCircle, Send, Users, Building2, 
  Shield, CheckCircle, Clock, Headphones, GraduationCap, Briefcase,
  Award, ArrowRight, Sparkles, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiry_type: 'general',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/enquiries`, formData);
      toast.success('Message sent successfully! We will contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', enquiry_type: 'general', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Quick Contact Options
  const quickContactOptions = [
    {
      icon: Phone,
      title: 'Call Us',
      value: '+91 9521484003',
      subtitle: 'Mon-Sat, 9AM-7PM',
      action: 'tel:+919521484003',
      color: 'blue',
      bgGradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: 'Chat Instantly',
      subtitle: 'Quick Response',
      action: 'https://wa.me/919521484003?text=Hi, I want to know more about Iqra Skills',
      color: 'emerald',
      bgGradient: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Mail,
      title: 'Email Us',
      value: 'info@iqraskills.org',
      subtitle: 'Get reply within 24hrs',
      action: 'mailto:info@iqraskills.org',
      color: 'purple',
      bgGradient: 'from-purple-500 to-pink-600'
    }
  ];

  // Why Contact Us
  const whyContactUs = [
    { icon: Headphones, title: 'Free Career Guidance', desc: 'Get personalized advice from our experts' },
    { icon: GraduationCap, title: 'Best Course Recommendation', desc: 'Find the perfect course for your goals' },
    { icon: Building2, title: 'Franchise Consultation', desc: 'Learn about partnership opportunities' },
    { icon: Briefcase, title: 'Job Placement Support', desc: 'Connect with hiring companies' }
  ];

  // Trust Stats
  const trustStats = [
    { icon: Users, value: '25,000+', label: 'Students Trained' },
    { icon: Building2, value: '100+', label: 'Training Centers' },
    { icon: Award, value: 'Govt', label: 'Affiliated' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 py-16 lg:py-24" data-testid="contact-hero">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Let's Connect &<br />
              <span className="text-blue-200">Build Your Future</span>
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-8">
              Have questions about courses, training centers, or career opportunities? We're here to help you.
            </p>

            {/* Hero CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a href="tel:+919521484003">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2" /> Call Now
                </Button>
              </a>
              <a href="https://wa.me/919521484003?text=Hi, I want to know more about Iqra Skills" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg w-full sm:w-auto"
                >
                  <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp Now
                </Button>
              </a>
            </div>

            {/* Response Time Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full text-sm">
              <Clock className="w-4 h-4" />
              Get response within 24 hours
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* QUICK CONTACT OPTIONS */}
      <section className="py-8 bg-white -mt-8 relative z-10" data-testid="quick-contact">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickContactOptions.map((option, index) => (
              <a
                key={index}
                href={option.action}
                target={option.action.startsWith('https') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                data-testid={`quick-contact-${index}`}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${option.bgGradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <option.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">{option.title}</h3>
                <p className={`text-${option.color}-600 font-semibold mb-1`}>{option.value}</p>
                <p className="text-slate-500 text-sm">{option.subtitle}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM + TRUST PANEL */}
      <section className="py-16 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Send Us a Message
                  </h2>
                  <p className="text-slate-600">Fill the form below and our team will get back to you shortly</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                      <Input 
                        id="name" 
                        placeholder="Enter your name"
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        required 
                        className="mt-1.5 h-12 border-slate-200 focus:border-blue-500"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700 font-medium">Mobile Number *</Label>
                      <Input 
                        id="phone" 
                        type="tel"
                        placeholder="+91 9876543210"
                        value={formData.phone} 
                        onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                        required 
                        className="mt-1.5 h-12 border-slate-200 focus:border-blue-500"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="you@example.com"
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        required 
                        className="mt-1.5 h-12 border-slate-200 focus:border-blue-500"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="enquiry_type" className="text-slate-700 font-medium">Enquiry Type</Label>
                      <Select value={formData.enquiry_type} onValueChange={(value) => setFormData({...formData, enquiry_type: value})}>
                        <SelectTrigger className="mt-1.5 h-12 border-slate-200" data-testid="contact-enquiry-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="course">Course Information</SelectItem>
                          <SelectItem value="franchise">Franchise / Partnership</SelectItem>
                          <SelectItem value="job">Job / Placement</SelectItem>
                          <SelectItem value="general">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700 font-medium">Your Message *</Label>
                    <Textarea 
                      id="message" 
                      rows={4} 
                      placeholder="Tell us how we can help you..."
                      value={formData.message} 
                      onChange={(e) => setFormData({...formData, message: e.target.value})} 
                      required 
                      className="mt-1.5 border-slate-200 focus:border-blue-500 resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-semibold rounded-xl"
                    data-testid="contact-submit"
                  >
                    {isLoading ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" /> Send Message
                      </>
                    )}
                  </Button>

                  {/* Security Note */}
                  <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                    <Shield className="w-4 h-4" />
                    <span>Your information is <strong className="text-slate-700">secure</strong> with us</span>
                  </div>
                </form>
              </div>
            </div>

            {/* Right: Trust Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Trust Stats */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white">
                <h3 className="text-lg font-bold mb-6">Why Trust Iqra Skills?</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {trustStats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <stat.icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <p className="text-xl font-bold text-blue-400">{stat.value}</p>
                      <p className="text-xs text-slate-400">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quote */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-slate-300 text-sm italic">
                    "Our team will contact you within 24 hours to assist with your queries"
                  </p>
                </div>
              </div>

              {/* Direct Contact Panel */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Need Instant Help?</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Skip the form and talk to our expert directly
                </p>

                <div className="space-y-3">
                  <a href="tel:+919521484003" className="block">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 h-12 justify-start gap-3 rounded-xl">
                      <Phone className="w-5 h-5" />
                      <span className="flex-1 text-left">Call: +91 9521484003</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                  <a href="https://wa.me/919521484003?text=Hi, I need help with Iqra Skills" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600 h-12 justify-start gap-3 rounded-xl">
                      <MessageCircle className="w-5 h-5" />
                      <span className="flex-1 text-left">WhatsApp Us</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>

                {/* Office Hours */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Office: Mon-Sat, 9:00 AM - 7:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP + LOCATION */}
      <section className="py-16 bg-white" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Visit Our Office
            </h2>
            <p className="text-lg text-slate-600">Come meet us at our headquarters</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Address Card */}
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-6 border border-slate-200">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Head Office</h3>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Near UCO Bank,<br />
                Bhartiya Link Road,<br />
                CHURU (RAJ) 331001
              </p>

              <a 
                href="https://www.google.com/maps/search/Near+UCO+Bank,+Bhartiya+Link+Road,+CHURU+RAJ+331001"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="w-full gap-2 rounded-xl border-blue-300 text-blue-600 hover:bg-blue-50">
                  <ExternalLink className="w-4 h-4" /> Open in Google Maps
                </Button>
              </a>
            </div>

            {/* Map Embed */}
            <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3520.5!2d74.6199!3d28.2961!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDE3JzQ2LjAiTiA3NMKwMzcnMTEuNiJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Iqra Skills Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CONTACT US */}
      <section className="py-16 bg-slate-50" data-testid="why-contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-3">
              <Sparkles className="w-4 h-4" /> Why Connect
            </span>
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Connect with Iqra Skills?
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyContactUs.map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-1 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden" data-testid="final-cta">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Still Confused?<br />Talk to Our Expert Now
          </h2>
          <p className="text-xl text-blue-100 mb-4">
            Get personalized guidance for courses, careers, and opportunities
          </p>
          <p className="text-blue-200 mb-10 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5" /> Limited seats available in ongoing batches
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919521484003">
              <Button 
                size="lg" 
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg w-full sm:w-auto"
              >
                <Phone className="w-5 h-5 mr-2" /> Call Now: 9521484003
              </Button>
            </a>
            <a href="https://wa.me/919521484003?text=Hi, I'm confused about which course to choose. Please help." target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg w-full sm:w-auto"
              >
                <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Sticky WhatsApp Button - Mobile */}
      <a 
        href="https://wa.me/919521484003?text=Hi, I want to know more about Iqra Skills"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-emerald-500 hover:bg-emerald-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
        data-testid="sticky-whatsapp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ContactPage;
